import React, { useEffect, useState } from "react";
import { useGlobalContext } from "context/global";
import { useDispatch } from "react-redux";
import Money from "components/money";
import Collapsible from "react-collapsible";
import useToggle from "hooks/useToggle";
import { Triggers } from "components/cart/summary";
import AddToCart from "components/add-to-cart";
import { updateActiveBox, selectAndSaveFrequency, beginCheckout } from "actions/cart";
import { useInView } from "react-intersection-observer";
import { isMobile } from "utils/device-detect";
import clsx from "clsx/lite";
import { areFrequenciesEqual } from "utils/frequency";
import { STICKY_ATC_VISIBLE } from "utils/action-types";

const SummaryBox = ({ title, bundle, cartSummary, hideCTALabel, isOneTimePurchase = false, className = "" }) => {
	const {
		translation,
		langCode,
		cartSettings: { shippingCost, boxes, subscription: { discountPercentage, discountCode, frequencies } }
	} = useGlobalContext();

	const {
		totalPrice, totalBowlsPrice, totalBoxPrice, firstOrderDiscount,
		showFreeShipping, pricePerMeal, highestBoxPrice
	} = cartSummary;
	const [isOpen, toggle] = useToggle();
	const dispatch = useDispatch();
	const [isATCSticky, setIsATCSticky] = useState(false);
	const [atcRef, atcInView] = useInView({ threshold: 1 });
	const isMobileView = isMobile();

	useEffect(() => {
		// Enable sticky ATC button if it's in view and not already sticky
		if (atcInView && !isATCSticky && isMobileView) {
			setIsATCSticky(true);
		}
	}, [atcInView]);

	useEffect(() => {
		if (!atcInView && isATCSticky && isMobileView)
			dispatch({ type: STICKY_ATC_VISIBLE, payload: true });
	}, [atcInView, isATCSticky, isMobileView]);

	const frequency = frequencies.find(f => areFrequenciesEqual(f, bundle.frequency));

	const handleATC = async () => {
		if (isOneTimePurchase)
			return dispatch(beginCheckout(langCode, true, discountCode));

		dispatch(selectAndSaveFrequency(frequency));
		dispatch(updateActiveBox(boxes, frequencies, true));
	};

	const atcProps = {
		callback: handleATC,
		showLoader: true,
		className: "py-4 px-8 btn-orange !w-full",
		product: bundle.product,
		text: translation?.other?.inCart,
		openCart: !isOneTimePurchase,
	};

	return (
		<>
			<div className={clsx("pb-4", className)}>
				<Collapsible
					transitionTime="200"
					onOpening={toggle}
					onClosing={toggle}
					className="!border-none"
					openedClassName="!border-none"
					trigger={
						Triggers({
							totalPrice, totalCompareAtPrice: totalBoxPrice,
							isOpen, showDescription: false, titleClass: "!text-base"
						})
					}
				>
					{totalBoxPrice > 0 &&
						<div className="flex border-y py-4 justify-between items-center">
							<span className="text-sm">{translation.cart?.box_price}</span>
							<div className="text-sm">
								{(totalBowlsPrice - totalBoxPrice) > 0 &&
									<span className="line-through mr-2 text-sm" aria-label={`Original price: ${totalBowlsPrice}`}>
										<Money value={totalBowlsPrice} />
									</span>}
								<Money value={totalBoxPrice} />
							</div>
						</div>}
					{firstOrderDiscount > 0 && discountPercentage && (
						<div className="flex justify-between items-center pt-4 pb-2">
							<span className="text-sm">
								{translation.cart?.first_order_discount} (-{discountPercentage}%)
							</span>
							<span className="text-sm"> -<Money value={firstOrderDiscount} /> </span>
						</div>
					)}
					<div className="flex justify-between items-center pb-4 md:pt-2">
						<span className="text-sm capitalize">{translation?.subscription?.shipping}</span>
						<span className="text-sm">
							{showFreeShipping ? "Free" : <Money value={shippingCost} />}
						</span>
					</div>
				</Collapsible>
			</div>
			{!atcInView && isATCSticky && isMobileView &&
				<StickyContainer
					title={title}
					hideCTALabel={hideCTALabel}
					pricePerMeal={pricePerMeal}
					highestBoxPrice={highestBoxPrice}
				>
					<AddToCart {...atcProps} />
				</StickyContainer>}
			<div ref={atcRef} className="w-full flex my-3 md:justify-start justify-center">
				<AddToCart {...atcProps} />
			</div>
		</>
	);
};

const StickyContainer = ({ pricePerMeal, hideCTALabel, highestBoxPrice, title, children }) => {
	const { translation } = useGlobalContext();

	return (
		<div className="is-sticky-bottom !m-0 left-0 border-t pb-1">
			{!hideCTALabel && <div className="flex justify-center text-small">
				<span>{title} {translation.other.for?.toLowerCase()}</span>
				<div className="mx-1">
					{highestBoxPrice !== pricePerMeal &&
						<span className="line-through text-small text-secondary" aria-label={`Original price: ${highestBoxPrice}`}>
							<Money value={highestBoxPrice} />
						</span>}
					<span className="font-boldParagraph text-small text-green-discount">
						{" "}<Money value={pricePerMeal} />
					</span>
				</div>
				<span>{translation.cart?.per_meal}</span>
			</div>}
			<div className="w-full flex my-3 md:justify-start justify-center px-5">
				{children}
			</div>
		</div>
	);
};

export default SummaryBox;